import { Component, OnInit } from "@angular/core";
import { BaseListDirective } from "../../../util/directives/base-list.directive";
import { EntityTyp } from "../../../util/enums/entity-typ.enum";
import { ContentHeader } from "../../../layout/components/content-header/content-header.component";
import { ActivatedRoute, Router } from "@angular/router";
import { ModalService } from "../../../util/service/modal.service";
import { FormBuilder, FormControl } from "@angular/forms";
import Swal from "sweetalert2";
import { colors } from "../../../colors.const";
import { NameDto, OfficeNameDto, ReportListItemDto } from "app/api/models";
import { HousingService, OfficeService, ReportService } from "app/api/services";
import { AuthService } from "../../../util/service/auth.service";
import { UserRole } from "app/server/enums/user-role.enum";
import { OfficeType } from "app/server/enums/office-type.enum";
import { ToastrService } from "ngx-toastr";
import { CaslAction } from "../../../server/enums/casl-action.enum";
import { formatDate } from "@angular/common";
import { EnumDropdownOption, EnumService } from "../../../util/service/enum.service";
import { DownloadService } from "../../../util/service/download.service";

@Component({
  selector: "app-report-list",
  templateUrl: "./report-list.component.html",
  styleUrl: "./report-list.component.scss",
  standalone: false,
})
export class ReportListComponent extends BaseListDirective<ReportListItemDto> implements OnInit {
  override modalType = EntityTyp.Report;
  protected readonly CaslAction = CaslAction;
  protected readonly EntityTyp = EntityTyp;

  subjects!: EnumDropdownOption[];
  housings: NameDto[];
  offices: OfficeNameDto[];

  createdDate: FormControl;
  updatedDate: FormControl;
  vorfallslisteOfficeFilterFormControl = new FormControl<number>(null);
  isExportingWord = false;

  public contentHeader: ContentHeader;

  constructor(
    protected override route: ActivatedRoute,
    protected override router: Router,
    private reportService: ReportService,
    protected override modalService: ModalService,
    protected override fb: FormBuilder,
    private housingService: HousingService,
    private officeService: OfficeService,
    private authService: AuthService,
    private toastrService: ToastrService,
    private enumService: EnumService,
    private downloadService: DownloadService
  ) {
    super(fb, route, router, modalService);
  }

  get isAdmin() {
    return this.authService.currentUserValue?.role === UserRole.Admin;
  }

  get hasGVSOffice() {
    return this.offices?.some(({ type }) => type === OfficeType.GVS);
  }

  get hasUmFOffice() {
    return this.offices?.some(({ type }) => type === OfficeType.umF);
  }

  get hasKJHOffice() {
    return this.offices?.some(({ type }) => type === OfficeType.KJH);
  }

  ngOnInit(): void {
    this.subjects = this.enumService.reportSubjectDropdownOptions;
    this.offices = this.route.snapshot.data["offices"];

    this.contentHeader = {
      headerTitle: "Berichte",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Startseite",
            isLink: true,
            link: "/",
            translate: "MENU.HOME",
          },
          {
            name: "Berichte",
            isLink: false,
            translate: "MENU.REPORTS",
          },
        ],
      },
    };

    this.initFilter({
      createdDateFrom: [null],
      createdDateTo: [null],
      updatedDateFrom: [null],
      updatedDateTo: [null],
      housingId: [null],
      officeId: [null],
      subject: [null],
      deleted: [false],
      affectedClientsSearch: [null],
    });

    if (this.isAdmin || this.hasGVSOffice) {
      this.housingService.housingControllerFindAllDropdown({ initial: true }).subscribe((housings) => {
        this.housings = housings;
      });
    }
    if (this.isAdmin) {
      this.officeService.officeControllerFindAllDropdown({ initial: true }).subscribe((offices) => {
        this.offices = offices;
      });
    }

    this.createdDate = this.registerDateRangeComponent("createdDateFrom", "createdDateTo");
    this.updatedDate = this.registerDateRangeComponent("updatedDateFrom", "updatedDateTo");
  }

  reload() {
    this.reportService.reportControllerFindAll(this.filter.value).subscribe({
      next: (page) => {
        this.page = page;
        this.syncPageNumber();
      },
    });
  }

  async openModal(id?: number) {
    await this.modalService.openEntityModal(EntityTyp.Report, id);
    this.reload();
  }

  async delete(id) {
    const confirm = await Swal.fire({
      text: "Möchten Sie diesen Bericht wirklich löschen?",
      icon: "error",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: colors.solid.danger,
      confirmButtonText: "Löschen",
      cancelButtonText: "Abbrechen",
    });
    if (confirm.isConfirmed) {
      this.reportService.reportControllerDelete({ id }).subscribe((res) => {
        this.reload();
      });
    }
  }

  async restore(id) {
    const confirm = await Swal.fire({
      text: "Möchten Sie diesen Bericht wirklich wiederherstellen?",
      icon: "question",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: colors.solid.info,
      confirmButtonText: "Wiederherstellen",
      cancelButtonText: "Abbrechen",
    });
    if (confirm.isConfirmed) {
      this.reportService.reportControllerRestore({ id }).subscribe((res) => {
        this.toastrService.success("Erfolgreich wiederhergestellt", "Erfolgreich wiederhergestellt");
        this.reload();
      });
    }
  }

  async downloadVorfallsliste() {
    const id = this.vorfallslisteOfficeFilterFormControl.value;
    if (id) {
      this.officeService.officeControllerGetIncidentsList({ id }).subscribe((res) => {
        if (res.size === 4) {
          return false;
        }
        const downloadAnchor = document.createElement("a");
        downloadAnchor.style.display = "none";

        downloadAnchor.href = URL.createObjectURL(res);
        const office = this.offices.find((o) => o.id === id);
        downloadAnchor.download = `Vorfallsliste_${office.name.replaceAll(/\s/g, "-")}_${formatDate(Date.now(), "MM-dd-yyyy", "de-AT")}.xlsx`;
        downloadAnchor.click();
      });
    }
  }

  public async openViewModal(filter = this.filter.value) {
    await this.modalService.openReportViewModal(filter);
  }

  exportWordSummary() {
    this.isExportingWord = true;
    this.downloadService.downloadWordSummary(this.filter.value).add(() => (this.isExportingWord = false));
  }
}
